import type { ReportQuery } from '@chukua/common';
import { lazy, Suspense } from 'react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../shared/components/SkeletonRegion';
import { useReportTrend } from '../hooks/use-report-trend';
import { toChartSeries } from '../policies/trend-series.policy';

const ReportTrendChart = lazy(() => import('./ReportTrendChart').then((module) => ({ default: module.ReportTrendChart })));

function ChartSkeleton() {
  return (
    <SkeletonRegion label="Loading trend chart">
      <Skeleton className="h-64 w-full" />
    </SkeletonRegion>
  );
}

/** One chart per currency under the figures. A failed load is thrown to the route's error page: a blank chart would read as "no billing". */
export function ReportTrendSection({ reportKey, query }: { reportKey: string; query: ReportQuery }) {
  const trend = useReportTrend(reportKey, query, true);
  if (trend.status === 'error') throw trend.error;
  if (trend.status === 'loading') return <ChartSkeleton />;
  const series = toChartSeries(trend.trend.series).filter((entry) => entry.points.length > 0);
  if (series.length === 0) return null;
  return (
    <section aria-label="Trend" className="mb-6 grid gap-4 lg:grid-cols-2">
      <Suspense fallback={<ChartSkeleton />}>
        {series.map((entry) => (
          <ReportTrendChart key={entry.label} series={entry} />
        ))}
      </Suspense>
    </section>
  );
}
